import { useEffect, useRef, useState } from "react";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* How many whole numbers we're willing to tick through one at a time before
   switching to a smooth sweep. A wallet balance of ₹2,48,422 obviously can't
   count 1-by-1; a user count of 4 should. */
const STEP_LIMIT = 60;
const STEP_MS = 90;      // dwell per integer in step mode
const SWEEP_MS = 900;    // total duration in sweep mode

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/**
 * Counts to `value` whenever it changes.
 *
 * Two modes, picked from the size of the change:
 *
 *  - Step mode (change of <= 60): renders **every** integer between the old
 *    and new value — 1, 2, 3, 4 — on a fixed cadence. Previously this used an
 *    eased sweep and then rounded the result, so an eased 0 -> 3 landed on
 *    frames like 0, 1.9, 2.7, 3 and rendered "0, 2, 3": numbers got skipped
 *    and small counters looked like they jumped. Stepping the integers
 *    directly is what makes the sequence complete.
 *  - Sweep mode (large changes, e.g. currency): eased interpolation, capped
 *    at ~0.9s, since ticking 248,422 individual values is neither possible
 *    at 60fps nor useful.
 */
const AnimatedNumber = ({ value = 0, duration, formatter }) => {
  const target = Number(value) || 0;

  const [display, setDisplay] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const clear = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
      rafRef.current = null;
      timerRef.current = null;
    };

    const from = fromRef.current;

    if (from === target) return undefined;

    if (prefersReducedMotion()) {
      fromRef.current = target;
      setDisplay(target);
      return undefined;
    }

    const startInt = Math.round(from);
    const endInt = Math.round(target);
    const distance = Math.abs(endInt - startInt);

    /* ---- Step mode: emit each integer in turn ---- */
    if (distance > 0 && distance <= STEP_LIMIT) {
      const direction = endInt > startInt ? 1 : -1;
      let current = startInt;
      const stepMs = duration ? Math.max(40, duration / distance) : STEP_MS;

      setDisplay(current);
      timerRef.current = setInterval(() => {
        current += direction;
        setDisplay(current);
        if (current === endInt) {
          clear();
          fromRef.current = endInt;
        }
      }, stepMs);

      return clear;
    }

    /* ---- Sweep mode: eased interpolation for large jumps ---- */
    const total = duration ?? SWEEP_MS;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min(1, (now - start) / total);
      setDisplay(from + (target - from) * easeOutCubic(progress));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
        setDisplay(target);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return clear;
  }, [target, duration]);

  const rounded = Math.round(display);

  return (
    <span className="animated-number">
      {formatter ? formatter(rounded) : rounded.toLocaleString("en-IN")}
    </span>
  );
};

export default AnimatedNumber;
